/**
 * Chart event tooltips are built as raw HTML by `combinedIcons` (see `src/helpers/utils.ts`) and
 * injected into chart plot line labels. That wrapper span is hostile to a tooltip:
 *
 *  - it carries a transform, so it becomes the containing block for `position: fixed` — viewport
 *    coordinates land in the wrong place;
 *  - the transform also makes it a stacking context, capping the tooltip's z-index below the chart's
 *    own series tooltip;
 *  - its width feeds back into a `width: auto` box's shrink-to-fit, collapsing the tooltip;
 *  - it sets `white-space: nowrap`, which the tooltip inherits, so text cannot wrap;
 *  - and pointer movement over the tooltip bubbles to the chart, popping the series tooltip on top.
 *
 * So the markup is copied into a single container appended to `<body>`, outside the chart entirely.
 * This module owns that container: showing, hiding and placing it.
 */

interface TriggerRect {
    left: number;
    top: number;
    right: number;
    bottom: number;
}

interface Size {
    width: number;
    height: number;
}

interface Viewport {
    width: number;
    height: number;
}

export const LAYER_ID = 'dbm-event-tooltip-layer';

/** Breathing room kept between the tooltip and the edge of the screen. */
export const EVENT_TOOLTIP_MARGIN = 8;

/**
 * How far the tooltip overlaps the hovered icons, so the pointer crosses onto it without traversing
 * dead space. The hide delay below would cover a gap, but an unbroken path does not depend on timing.
 */
export const EVENT_TOOLTIP_OVERLAP = 2;

/** Grace period so the pointer can travel between the icons and the tooltip. */
export const HIDE_DELAY_MS = 120;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), Math.max(min, max));

/**
 * Places the tooltip beside the hovered icons without letting it leave the viewport.
 *
 * Flipping side is not enough on its own — a wide tooltip can overflow after flipping too —
 * so the flip only decides the preferred side and the clamp guarantees both edges stay on screen.
 */
export const computeEventTooltipPosition = (trigger: TriggerRect, size: Size, viewport: Viewport) => {
    const openRightAt = trigger.right - EVENT_TOOLTIP_OVERLAP;
    const fitsOnTheRight = openRightAt + size.width <= viewport.width - EVENT_TOOLTIP_MARGIN;
    const preferredLeft = fitsOnTheRight
        ? openRightAt
        : trigger.left - size.width + EVENT_TOOLTIP_OVERLAP;

    return {
        left: clamp(preferredLeft, EVENT_TOOLTIP_MARGIN, viewport.width - size.width - EVENT_TOOLTIP_MARGIN),
        top: clamp(trigger.top, EVENT_TOOLTIP_MARGIN, viewport.height - size.height - EVENT_TOOLTIP_MARGIN)
    };
};

let currentTrigger: Element | null = null;
let hideTimer: number | undefined;

const cancelHide = () => window.clearTimeout(hideTimer);

const hide = () => {
    currentTrigger = null;
    const layer = document.getElementById(LAYER_ID);
    if (!layer) return;

    layer.style.display = 'none';
    layer.innerHTML = '';
};

const scheduleHide = () => {
    cancelHide();
    hideTimer = window.setTimeout(hide, HIDE_DELAY_MS);
};

const getLayer = (): HTMLElement => {
    const existing = document.getElementById(LAYER_ID);
    if (existing) return existing;

    const layer = document.createElement('div');
    layer.id = LAYER_ID;
    // Reuses .event-tooltip so the styling stays in one place; the modifier marks it body-level.
    layer.className = 'event-tooltip event-tooltip-layer';
    layer.addEventListener('mouseenter', cancelHide);
    layer.addEventListener('mouseleave', scheduleHide);
    document.body.appendChild(layer);

    return layer;
};

const show = (trigger: HTMLElement, content: HTMLElement) => {
    const layer = getLayer();

    if (currentTrigger !== trigger) {
        // A copy of markup this app already rendered into the chart — no new content is introduced.
        layer.innerHTML = content.innerHTML;
        // Preserves the old `.execution-plan-samples-chart .event-tooltip` z-index intent, which can
        // no longer apply by descendant selector now that the tooltip lives outside the chart.
        layer.classList.toggle(
            'event-tooltip-layer--plan-samples',
            !!trigger.closest('.execution-plan-samples-chart')
        );
        currentTrigger = trigger;
    }

    layer.style.display = 'block';

    const { offsetWidth: width, offsetHeight: height } = layer;
    if (!width || !height) return;

    const { left, top } = computeEventTooltipPosition(
        trigger.getBoundingClientRect(),
        { width, height },
        { width: window.innerWidth, height: window.innerHeight }
    );

    layer.style.left = `${left}px`;
    layer.style.top = `${top}px`;
};

const handleMouseOver = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    const trigger = target?.closest?.('.hover-trigger') as HTMLElement | null;
    if (!trigger) return;

    const content = trigger.nextElementSibling as HTMLElement | null;
    if (!content?.classList.contains('event-tooltip')) return;

    cancelHide();
    show(trigger, content);
};

const handleMouseOut = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target?.closest?.('.hover-trigger')) return;

    // Moving onto the tooltip itself must not dismiss it.
    const related = event.relatedTarget;
    if (related instanceof Element && related.closest(`#${LAYER_ID}`)) return;

    scheduleHide();
};

/**
 * Starts serving chart event tooltips from the body-level layer. Returns the teardown function.
 */
export const initEventTooltipLayer = () => {
    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);
    // A fixed tooltip would otherwise hang in place while the chart underneath it moves.
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);

    return () => {
        document.removeEventListener('mouseover', handleMouseOver);
        document.removeEventListener('mouseout', handleMouseOut);
        window.removeEventListener('scroll', hide, true);
        window.removeEventListener('resize', hide);
        cancelHide();
        document.getElementById(LAYER_ID)?.remove();
        currentTrigger = null;
    };
};
